import { useState } from 'react'
import { Eye, EyeOff, Edit2, Copy, Check, MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import ButtonGerarAnlise from './bnt-analise-simples'
import { Product } from '../../state/selectProduct'
import { useUpdatePasswordProduct } from '../../services/useUpdatePasswordProduct'
import { useCurrentUser } from '@/features/auth/state/auth'

interface InfoSimplesProps {
  product: Product
  cnpj: string
  showCNPJ?: boolean
  showCPF?: boolean
  showAccessCode?: boolean
}

export default function InfoSimples({ product, cnpj, showAccessCode = true }: InfoSimplesProps) {
  const user = useCurrentUser()
  const [showAccessCodeValue, setShowAccessCodeValue] = useState(false)
  const [isEditingAccessCode, setIsEditingAccessCode] = useState(false)
  const [accessCode, setAccessCode] = useState(product.senhaProduto || '')
  const [tempAccessCode, setTempAccessCode] = useState(accessCode)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const updatePassword = useUpdatePasswordProduct()

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }
  const handleSaveAccessCode = async () => {
    try {
      await updatePassword.mutateAsync({
        senha: tempAccessCode,
        id_product: product.id
      })
      setAccessCode(tempAccessCode)
      setIsEditingAccessCode(false)
      console.log('Código de acesso atualizado com sucesso!')
    } catch (error) {
      console.error('Erro ao atualizar código de acesso:', error)
    }
  }
  console.log(product)
  const handleCancelEdit = () => {
    setTempAccessCode(accessCode)
    setIsEditingAccessCode(false)
  }

  return (
    <TooltipProvider>
      <Card className="min-w-[300px] shadow-sm border border-gray-200 mb-8">
        <CardContent className="p-2 sm:p-4 bg-background">
          <div className="flex flex-col items-center sm:flex-row sm:justify-between sm:items-center gap-4 w-full">
            {showAccessCode && (
              <div className="w-full sm:w-auto space-y-2 max-w-[260px]">
                <label className="text-sm font-medium text-gray-700">CÓDIGO DE ACESSO</label>
                <div className="bg-gray-50 rounded-lg border p-2">
                  {isEditingAccessCode ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={tempAccessCode}
                        onChange={e => setTempAccessCode(e.target.value)}
                        className="text-base sm:text-lg font-mono bg-white w-full"
                        maxLength={12}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-green-600 hover:bg-green-50 flex-shrink-0"
                        onClick={handleSaveAccessCode}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 flex-shrink-0"
                        onClick={handleCancelEdit}
                      >
                        ×
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="text-base sm:text-lg font-mono text-gray-900 truncate pr-2">
                        {showAccessCodeValue ? accessCode : '••••••••••••'}
                      </p>
                      {/* Desktop */}
                      <div className="hidden sm:flex items-center gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-white"
                              onClick={() => setShowAccessCodeValue(!showAccessCodeValue)}
                            >
                              {showAccessCodeValue ? (
                                <EyeOff className="h-4 w-4 text-gray-500" />
                              ) : (
                                <Eye className="h-4 w-4 text-gray-500" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{showAccessCodeValue ? 'Ocultar' : 'Mostrar'}</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-white"
                              onClick={() => setIsEditingAccessCode(true)}
                            >
                              <Edit2 className="h-4 w-4 text-gray-500" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Editar código</p>
                          </TooltipContent>
                        </Tooltip>

                        {showAccessCodeValue && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-white"
                                onClick={() => copyToClipboard(accessCode, 'accessCode')}
                              >
                                {copiedField === 'accessCode' ? (
                                  <Check className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Copy className="h-4 w-4 text-gray-500" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{copiedField === 'accessCode' ? 'Copiado!' : 'Copiar código'}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>

                      {/* Mobile dropdown */}
                      <div className="sm:hidden ">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4 text-gray-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setShowAccessCodeValue(!showAccessCodeValue)}>
                              {showAccessCodeValue ? (
                                <>
                                  <EyeOff className="h-4 w-4 mr-2" />
                                  Ocultar
                                </>
                              ) : (
                                <>
                                  <Eye className="h-4 w-4 mr-2" />
                                  Mostrar
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setIsEditingAccessCode(true)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Editar código
                            </DropdownMenuItem>
                            {showAccessCodeValue && (
                              <DropdownMenuItem onClick={() => copyToClipboard(accessCode, 'accessCode')}>
                                <Copy className="h-4 w-4 mr-2" />
                                {copiedField === 'accessCode' ? 'Copiado!' : 'Copiar código'}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {user?.typeUser === 'ADMIN' && <ButtonGerarAnlise cnpj={cnpj} />}
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  )
}
